import { db } from './db.js';
import { Emitter, normalizeText } from './utils.js';

/* Lo que Filoteca deja de mostrar tras una denuncia de contenido (server/reports.js): películas de «Cine clásico»,
   listas públicas enteras y canales sueltos de una lista. La lista la da el servidor (GET /api/reports/blocklist); aquí se
   guarda la última recibida para que funcione sin conexión. Sin servidor o sin red, se usa la última conocida. */

const KEY = 'content-blocklist';
const EMPTY = { ids: [], lists: [], channels: [] };

class ContentBlock extends Emitter {
    constructor() {
        super();
        this.data = EMPTY;
        this._ids = new Set();
        this._lists = new Set();
        this._channels = new Set();
        this._loaded = false;
    }

    _set(d) {
        const data = { ids: Array.isArray(d.ids) ? d.ids.map(String) : [], lists: Array.isArray(d.lists) ? d.lists.map(String) : [], channels: Array.isArray(d.channels) ? d.channels.filter((c) => c && c.list && c.name) : [] };
        const before = JSON.stringify(this.data);
        this.data = data;
        this._ids = new Set(data.ids);
        this._lists = new Set(data.lists);
        this._channels = new Set(data.channels.map((c) => c.list + '|' + normalizeText(c.name)));
        return before !== JSON.stringify(data);
    }

    get any() { return this._ids.size + this._lists.size + this._channels.size > 0; }

    /** Película de «Cine clásico» (el id puede llevar el prefijo del proveedor: «<proveedor>~ia-…»). */
    isFilmBlocked(id) {
        const s = String(id || '');
        const raw = s.indexOf('~') === -1 ? s : s.slice(s.indexOf('~') + 1);
        return this._ids.has(raw);
    }

    isListBlocked(listId) { return this._lists.has(String(listId || '')); }

    isChannelBlocked(listId, name) { return this._channels.has(String(listId || '') + '|' + normalizeText(name || '')); }

    /** Carga lo guardado (sin red) y, si se puede, lo pide al servidor (`base` = dirección del servidor de Filoteca). Avisa con 'change' si algo cambió. */
    async refresh(base = '') {
        if (!this._loaded) {
            this._loaded = true;
            const saved = await db.get('meta', KEY).catch(() => null);
            if (saved && this._set(saved)) this.emit('change');
        }
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
        try {
            const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
            const timer = setTimeout(() => ctl && ctl.abort(), 6000);
            let r;
            try {
                r = await fetch(String(base || '').replace(/\/+$/, '') + '/api/reports/blocklist', { cache: 'no-store', signal: ctl ? ctl.signal : undefined });
            } finally {
                clearTimeout(timer);
            }
            const json = await r.json().catch(() => null);
            if (!r.ok || !json || json.ok !== true) return; // un servidor sin denuncias contesta con la web, no con JSON
            const changed = this._set(json);
            await db.put('meta', KEY, this.data).catch(() => {});
            if (changed) this.emit('change');
        } catch (e) { /* sin red: se queda lo último que se sabía */ }
    }
}

export const contentBlock = new ContentBlock();

/** Copia del catálogo de un proveedor sin lo denunciado. `listId` = id de la lista pública del proveedor (o '' si no lo es). */
export function withoutBlocked(data, { listId = '', isArchive = false } = {}) {
    if (!data || !contentBlock.any) return data;
    if (listId && contentBlock.isListBlocked(listId)) return Object.assign({}, data, { live: [], vod: [], series: [] });
    let live = data.live;
    let vod = data.vod;
    if (listId) live = (data.live || []).filter((c) => !contentBlock.isChannelBlocked(listId, c.name));
    if (isArchive) vod = (data.vod || []).filter((m) => !contentBlock.isFilmBlocked(m.id));
    return live === data.live && vod === data.vod ? data : Object.assign({}, data, { live, vod });
}
